import { apiClient } from '../lib/apiClient';

/**
 * AI credits of the signed-in user (backend src/routes/aiUsage.routes.ts):
 *   GET /api/v1/me/ai-usage → { data: AiUsageSummary }
 *
 * AI endpoints refuse with
 *   429 AI_BUDGET_EXCEEDED          — the user's monthly credits are used up
 *                                     (error.details.resetsAt = next reset, ISO)
 *   503 AI_TEMPORARILY_UNAVAILABLE  — the app-wide monthly AI budget is reached
 */

export type AiPlan = 'free' | 'reef' | 'reef_pro';

export interface AiUsageSummary {
  readonly plan: AiPlan;
  /** 'YYYY-MM' (UTC). */
  readonly month: string;
  readonly creditsUsed: number;
  /** Plan credits + top-ups + available welcome credit; null = unlimited. */
  readonly creditsBudget: number | null;
  /** Admin top-ups of this month (the welcome credit is separate). */
  readonly topupCredits: number;
  /**
   * The part of creditsBudget that comes from the one-time welcome credit (what is
   * left of it this month; 0 when none or expired). Optional: older APIs omit it.
   */
  readonly welcomeCredits?: number;
  /** welcomeCredits minus this month's usage (the welcome credit is used first). */
  readonly welcomeRemaining?: number;
  /** ISO end of the welcome credit's 30-day window; null when none or expired. */
  readonly welcomeExpiresAt?: string | null;
  readonly percent: number;
  /** ISO timestamp of the next monthly reset. */
  readonly resetsAt: string;
  readonly warning: boolean;
  readonly exceeded: boolean;
}

export const AI_BUDGET_EXCEEDED = 'AI_BUDGET_EXCEEDED';
export const AI_TEMPORARILY_UNAVAILABLE = 'AI_TEMPORARILY_UNAVAILABLE';

/** Thrown when /me/ai-usage answers 2xx with a body that is not the documented contract. */
export class AiUsageContractError extends Error {
  constructor() {
    super('Unexpected AI usage response shape');
    this.name = 'AiUsageContractError';
  }
}

function assertAiUsage(value: unknown): AiUsageSummary {
  const v = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
  if (
    v === null ||
    typeof v.creditsUsed !== 'number' ||
    (v.creditsBudget !== null && typeof v.creditsBudget !== 'number') ||
    typeof v.percent !== 'number' ||
    typeof v.resetsAt !== 'string'
  ) {
    throw new AiUsageContractError();
  }
  return value as AiUsageSummary;
}

export async function getAiUsage(): Promise<AiUsageSummary> {
  const response = await apiClient.get<{ data: unknown }>('/api/v1/me/ai-usage');
  return assertAiUsage(response.data);
}

export type AiLimitErrorKind = 'exceeded' | 'unavailable';

function field(error: unknown, key: string): unknown {
  return typeof error === 'object' && error !== null ? (error as Record<string, unknown>)[key] : undefined;
}

/** Which AI limit refused a request, or null when the error is something else. */
export function getAiLimitErrorKind(error: unknown): AiLimitErrorKind | null {
  const code = field(error, 'code');
  if (code === AI_BUDGET_EXCEEDED) return 'exceeded';
  if (code === AI_TEMPORARILY_UNAVAILABLE) return 'unavailable';
  return null;
}

/** error.details.resetsAt of an AI_BUDGET_EXCEEDED response (ApiError.data), if present. */
export function getAiLimitResetsAt(error: unknown): string | null {
  const body = field(error, 'data');
  const details = field(field(body, 'error'), 'details');
  const resetsAt = field(details, 'resetsAt');
  return typeof resetsAt === 'string' ? resetsAt : null;
}
